import React, { useEffect, useState } from "react";
import { ReactComponent as DownloadIcon } from "./../../../assets/icons/download.svg";
import { ReactComponent as UserIcon } from "./../../../assets/icons/user.svg";
import { getAllVerifications, useAppDispatch } from "../../../redux";
import { useSelector } from "react-redux";
import VerificationRequestModal from "../modals/VerificationRequestModal";
import { VerificationStatusEnum } from "../../shared/model";
import Button from "../../../components/ui/Button";
import Card from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { AlertTriangle, CheckCircle, Clock, FileText, Filter, Search } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { selectLoaderValue } from "../../../redux/Slices/loadingSlice";
import Spinner from "../../shared/components/Spinner";
const statusStyles = {
    [VerificationStatusEnum.pending]: "bg-yellow-100 text-yellow-700",
    [VerificationStatusEnum.accepted]: "bg-green-100 text-green-700",
    [VerificationStatusEnum.rejected]: "bg-red-100 text-red-700",
};

const typeLabels = {
    identityFiles: "Identity",
    businessFiles: "Business",
    addressProofFiles: "Address",
};
const VerificationRequestComponent = () => {
    const dispatch = useAppDispatch();
    const isLoading = useSelector(selectLoaderValue);
    const [open, setOpen] = useState(false);
    const { verifications } = useSelector((state: any) => state.admin);
    const [currentUserInfo, setCurrentUserInfo] = useState({});
    const [clickedItem, setClickedItem] = useState(null);
    const [requestType, setRequestType] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [sortBy, setSortBy] = useState("newest");
    const [status, setStatus] = useState("all");
    const [reports, setReports] = useState({
        total: 0,
        pending: 0,
        accepted: 0,
        rejected: 0,
    });

    useEffect(() => {
        getAllVerificationUsers();
    }, []);
    const getAllVerificationUsers = async () => {
        await dispatch(getAllVerifications({}));
    };
    const getStatusInfo = (verification: any, type: string) => {
        const statusField = type.replace("Files", "Status"); // e.g. identityFiles -> identityStatus
        const statusObj = verification[statusField];
        const statusId = statusObj?.id ? parseInt(statusObj.id, 10) : VerificationStatusEnum.pending;

        // Map enum id to string (capitalized)
        const statusText =
            Object.keys(VerificationStatusEnum).find(
                key => VerificationStatusEnum[key as keyof typeof VerificationStatusEnum] === statusId
            ) || "pending";

        return {
            text: statusText.charAt(0).toUpperCase() + statusText.slice(1), // capitalize
            className: statusStyles[statusId] || statusStyles[VerificationStatusEnum.pending],
        };
    };

    const verificationRequests: any[] = [];
    verifications?.map((verification: any, index: number) => {
        ["identity", "business", "addressProof"].forEach(docType => {
            const files = verification[docType + "Files"];
            if (files?.length) {
                verificationRequests.push(verification);
            }
        });
    });
    
    // setReports(prev => ({
    //     ...prev, // preserve existing privacy settings
    //     total: verificationRequests.length,
    //     // pending: ve
    // }));

    return (
        <>
            {isLoading && (
                <div className="fixed left-[50%] top-[40%] z-[1000px]">
                    <Spinner color="green" />
                </div>
            )}
            <VerificationRequestModal
                isOpen={open}
                onClose={() => setOpen(false)}
                request={currentUserInfo}
                clickedItem={clickedItem}
                onRefresh={getAllVerificationUsers}
            />
            {/*  lg:pl-64  */}
            <main className={`flex-1 pt-16 ${isLoading ? "opacity-25" : "opacity-100"}`}>
                <div className="p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Verification Requests</h1>
                            <p className="text-gray-600 mt-1">
                                Review and manage verification requests from professionals
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                className="
                                inline-flex items-center justify-center rounded-md text-sm font-medium
                                transition-colors focus-visible:outline-none focus-visible:ring-2
                                focus-visible:ring-blue-500 disabled:pointer-events-none disabled:opacity-50
                                border border-gray-200 bg-white text-gray-700 hover:bg-gray-50
                                h-10 px-4 py-2 flex items-center gap-2">
                                <DownloadIcon />
                                Export List
                            </button>
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1  gap-4">
                        {/*  style="opacity: 1;" */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <Card color={undefined} className="p-6">
                                <div className="flex justify-between items-start">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                        <FileText className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <Badge variant={undefined}>Past 30 days</Badge>
                                </div>
                                <h3 className="text-2xl font-semibold mt-4">{verificationRequests?.length}</h3>
                                <p className="text-gray-600">Total Requests</p>
                            </Card>

                            <Card color={undefined} className="p-6">
                                <div className="flex justify-between items-start">
                                    <div className="p-2 bg-yellow-100 rounded-lg">
                                        <Clock className="h-5 w-5 text-yellow-600" />
                                    </div>
                                    <Badge variant={undefined}>Past 30 days</Badge>
                                </div>
                                <h3 className="text-2xl font-semibold mt-4">12</h3>
                                <p className="text-gray-600">Pending Review</p>
                            </Card>

                            <Card color={undefined} className="p-6">
                                <div className="flex justify-between items-start">
                                    <div className="p-2 bg-green-100 rounded-lg">
                                        <CheckCircle className="h-5 w-5 text-green-600" />
                                    </div>
                                    <Badge variant={undefined}>Past 30 days</Badge>
                                </div>
                                <h3 className="text-2xl font-semibold mt-4">8</h3>
                                <p className="text-gray-600">Approved</p>
                            </Card>

                            <Card className="p-6" color={undefined}>
                                <div className="flex justify-between items-start">
                                    <div className="p-2 bg-red-100 rounded-lg">
                                        <AlertTriangle className="h-5 w-5 text-red-600" />
                                    </div>
                                    <Badge variant={undefined}>Past 30 days</Badge>
                                </div>
                                <h3 className="text-2xl font-semibold mt-4">5</h3>
                                <p className="text-gray-600">Rejected</p>
                            </Card>
                        </div>
                        {/* style="opacity: 1;" */}
                        <div className="mt-6 rounded-xl border shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1 bg-white border-gray-100 overflow-hidden">
                            <div className="p-4 border-b">
                                <div className="flex flex-col sm:flex-row justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="relative flex-1 sm:min-w-[300px]">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Search requests..."
                                                value={searchQuery}
                                                onChange={e => setSearchQuery(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2 border rounded-lg"
                                            />
                                        </div>
                                        <Button
                                            variant="outline"
                                            onClick={() => setShowFilters(!showFilters)}
                                            className="flex items-center gap-2">
                                            <Filter className="h-5 w-5" />
                                            Filters
                                        </Button>
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <select
                                            value={sortBy}
                                            onChange={e => setSortBy(e.target.value)}
                                            className="px-4 py-2 border rounded-lg">
                                            <option value="newest">Newest First</option>
                                            <option value="oldest">Oldest First</option>
                                            <option value="priority">Priority</option>
                                            <option value="deadline">Deadline</option>
                                        </select>
                                    </div>
                                </div>

                                <AnimatePresence>
                                    {showFilters && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="mt-4 pt-4 border-t">
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Request Type
                                                    </label>
                                                    <select
                                                        value={requestType}
                                                        onChange={e => setRequestType(e.target.value)}
                                                        className="w-full px-4 py-2 border rounded-lg">
                                                        <option value="all">All Types</option>
                                                        <option value="identity">Identity Verification</option>
                                                        <option value="business">Business Verification</option>
                                                        <option value="professional">Professional License</option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Status
                                                    </label>
                                                    <select
                                                        value={status}
                                                        onChange={e => setStatus(e.target.value)}
                                                        className="w-full px-4 py-2 border rounded-lg">
                                                        <option value="all">All Status</option>
                                                        <option value="pending">Pending</option>
                                                        <option value="in_review">In Review</option>
                                                        <option value="approved">Approved</option>
                                                        <option value="rejected">Rejected</option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Professional Type
                                                    </label>
                                                    <select className="w-full px-4 py-2 border rounded-lg">
                                                        <option value="all">All Types</option>
                                                        <option value="local">Local Professional</option>
                                                        <option value="international">
                                                            International Professional
                                                        </option>
                                                    </select>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Request ID
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Professional
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Type
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Priority
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Deadline
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {verifications?.map((verification: any) => {
                                            // Build rows per document type if they have files
                                            const rows = [];

                                            ["identityFiles", "businessFiles", "addressProofFiles"].forEach(docType => {
                                                if (verification[docType]?.length) {
                                                    const statusInfo = getStatusInfo(verification, docType);
                                                    rows.push(
                                                        <tr
                                                            key={`${verification.id}-${docType}`}
                                                            className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {verification.id}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="flex items-center">
                                                                    <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                                                                        {/* Replace with UserIcon or user's photo */}
                                                                        {verification.userId?.photo?.path ? (
                                                                            <img
                                                                                src={verification.userId.photo.path}
                                                                                alt={`${verification.userId.firstName} avatar`}
                                                                                className="h-10 w-10 rounded-full object-cover"
                                                                            />
                                                                        ) : (
                                                                            <UserIcon className="h-5 w-5 text-gray-400" />
                                                                        )}
                                                                    </div>
                                                                    <div className="ml-4">
                                                                        <div className="font-medium text-gray-900">
                                                                            {verification.userId?.firstName}{" "}
                                                                            {verification.userId?.lastName}
                                                                        </div>
                                                                        <div className="text-sm text-gray-500">
                                                                            {/* Assuming hourlyRate or role could go here */}
                                                                            {verification.userId?.role?.id === "4"
                                                                                ? "Professional"
                                                                                : "User"}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize bg-blue-100 text-blue-800 hover:bg-blue-200">
                                                                    {typeLabels[docType]}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div
                                                                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors hover:bg-yellow-200 ${statusInfo.className}`}>
                                                                    {statusInfo.text}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-red-100 text-red-700">
                                                                    High{" "}
                                                                    {/* You can replace with actual priority field if available */}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {/* Assuming createdAt is the deadline? Replace if you have a deadline field */}
                                                                {new Date(verification.createdAt).toLocaleDateString()}
                                                            </td>
                                                            <td className=" py-4 whitespace-nowrap text-right text-sm font-medium  flex">
                                                                <button
                                                                    className="inline-flex items-center justify-center rounded-md text-sm font-medium
                          transition-colors focus-visible:outline-none focus-visible:ring-2
                          focus-visible:ring-blue-500 disabled:pointer-events-none disabled:opacity-50
                          hover:bg-gray-100 h-8 px-3 text-blue-600 hover:text-blue-700"
                                                                    onClick={() => {
                                                                        setCurrentUserInfo(verification);
                                                                        setOpen(true);
                                                                        setClickedItem(docType); // capture the document type clicked

                                                                        // Optionally set state here to show details modal
                                                                    }}>
                                                                    View Details
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                            });

                                            return rows;
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div className="px-6 py-4 border-t flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-700">Showing page 1 of 10</span>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        className="
                                        inline-flex items-center justify-center rounded-md text-sm font-medium
                                        transition-colors focus-visible:outline-none focus-visible:ring-2
                                        focus-visible:ring-blue-500 disabled:pointer-events-none disabled:opacity-50
                                        border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 h-8 px-3"
                                        disabled>
                                        Previous
                                    </button>
                                    <button
                                        className="
                                        inline-flex items-center justify-center rounded-md text-sm font-medium
                                        transition-colors focus-visible:outline-none focus-visible:ring-2
                                        focus-visible:ring-blue-500 disabled:pointer-events-none disabled:opacity-50
                                        border border-gray-200 bg-white text-gray-700 hover:bg-gray-50
                                        h-8 px-3">
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};
export default VerificationRequestComponent;
